import type {IWuIcons} from '@npm-questionpro/wick-ui-lib'
import type {TTaskStatus} from '../../../types'
import {GLOBAL} from '../../../Translation/English/global'

/** Mirrors the backend `TaskStatusEnum`. These strings are the wire values —
 *  they are sent to `POST /tasks` and `PUT /tasks/status` verbatim. */
export const TASK_STATUS = {
  pending: 'Pending',
  inProgress: 'In progress',
  completed: 'Completed',
  closed: 'Closed',
} as const

interface ITaskStatusMeta {
  /** Translation key resolved with `wt()` at the render site. */
  labelKey: string
  /** Wick icon name, taken from the Initiative Detail v4 design's own STATUS map. */
  icon: IWuIcons
}

const STATUS_META: Record<TTaskStatus, ITaskStatusMeta> = {
  [TASK_STATUS.pending]: {
    labelKey: GLOBAL.pending,
    icon: 'wm-radio-button-unchecked',
  },
  [TASK_STATUS.inProgress]: {
    labelKey: GLOBAL.inProgress,
    icon: 'wm-pending',
  },
  [TASK_STATUS.completed]: {
    labelKey: GLOBAL.completed,
    icon: 'wm-check-circle',
  },
  [TASK_STATUS.closed]: {
    labelKey: GLOBAL.closed,
    icon: 'wm-cancel',
  },
}

/** Display order, and the rank used to group the list. */
const STATUS_ORDER: TTaskStatus[] = [
  TASK_STATUS.pending,
  TASK_STATUS.inProgress,
  TASK_STATUS.completed,
  TASK_STATUS.closed,
]

export const taskConstants = {
  TASK_STATUS,
  STATUS_META,
  STATUS_ORDER,
  /** A task in one of these has nothing left to chase: no overdue, no stale. */
  TERMINAL_STATUSES: [
    TASK_STATUS.completed,
    TASK_STATUS.closed,
  ] as TTaskStatus[],
  /** Days without an `updatedAt` change before a task is called out as stale. */
  STALE_DAYS: 5,
  /** `POST /initiatives/with-tasks` caps at 100; `/tasks/bulk` has no server
   *  cap, so the client applies the same limit rather than posting unbounded. */
  MAX_BULK_TASKS: 100,
  /** New tasks land a week out — matches the server's DEFAULT_TASK_DUE_DAYS. */
  DEFAULT_DUE_DAYS: 7,
  /** The team strip is a glance, not a directory — cap the cards it renders. */
  TEAM_STRIP_MAX_OWNERS: 8,
  /** `CreateActionCommentInputDto` caps the trimmed text at this. */
  MAX_COMMENT_LENGTH: 10_000,
  /** `UpdateTaskBlockInputDto` caps the trimmed reason at this. */
  MAX_BLOCKED_REASON_LENGTH: 1_000,
  /** The blocked chip's tooltip previews the reason up to this many characters. */
  BLOCKED_REASON_PREVIEW_LENGTH: 120,
  /** The composer's character counter stays out of the way until here. */
  COMMENT_COUNTER_FROM: 9_000,
  /** A comment reads as "(edited)" only when `updatedAt` is at least this far
   *  past `createdAt`, so write jitter on create never shows as an edit. */
  COMMENT_EDIT_MARGIN_MS: 1_000,
} as const
